import React, { useMemo } from "react";
import {
  Avatar,
  Box,
  Chip,
  LinearProgress,
  Typography,
  useTheme,
} from "@mui/material";
import type { Node } from "@xyflow/react";
import { Handle, Position, type NodeProps } from "@xyflow/react";
import { Goal } from "../../lib/types";
import { GOAL_ICONS } from "./GoalForm";

export type GoalGraphNodeData = {
  goal: Goal;
};

export type GoalGraphNodeType = Node<GoalGraphNodeData, "goal">;

const formatCurrency0 = (amount: number) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);

const getProgressColor = (progressPct: number) => {
  if (progressPct >= 100) return "success" as const;
  if (progressPct >= 75) return "primary" as const;
  if (progressPct >= 50) return "info" as const;
  if (progressPct >= 25) return "warning" as const;
  return "error" as const;
};

const normalizeCategories = (goal: Goal): string[] => {
  const raw = Array.isArray(goal.categories) ? goal.categories : [];
  const cleaned = raw.map((c) => String(c || "").trim()).filter(Boolean);
  const seen = new Set<string>();
  const result: string[] = [];
  for (const c of cleaned) {
    const key = c.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    result.push(c);
  }
  return result;
};

const getDaysRemaining = (targetDate: Date) => {
  const today = new Date();
  const diffTime = targetDate.getTime() - today.getTime();
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
};

const GoalGraphNode = ({ data, selected }: NodeProps<GoalGraphNodeType>) => {
  const theme = useTheme();
  const goal = data.goal;

  const progressPct = useMemo(() => {
    if (!Number.isFinite(goal.targetAmount) || goal.targetAmount <= 0) return 0;
    return Math.min((goal.currentAmount / goal.targetAmount) * 100, 100);
  }, [goal.currentAmount, goal.targetAmount]);

  const iconConfig =
    GOAL_ICONS[goal.icon as keyof typeof GOAL_ICONS] || GOAL_ICONS.other;
  const categories = normalizeCategories(goal);

  const daysRemaining = useMemo(
    () => getDaysRemaining(new Date(goal.targetDate)),
    [goal.targetDate]
  );

  const borderColor = selected
    ? theme.palette.primary.main
    : theme.palette.divider;

  return (
    <Box
      sx={{
        width: 320,
        p: 1.5,
        borderRadius: 2,
        bgcolor: theme.palette.background.paper,
        border: `2px solid ${borderColor}`,
        boxShadow: selected ? theme.shadows[6] : theme.shadows[2],
        position: "relative",
      }}
    >
      <Handle
        type="target"
        position={Position.Left}
        style={{ width: 10, height: 10, borderRadius: 20 }}
      />
      <Handle
        type="source"
        position={Position.Right}
        style={{ width: 10, height: 10, borderRadius: 20 }}
      />

      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.25 }}>
        <Avatar
          sx={{
            width: 40,
            height: 40,
            bgcolor: iconConfig.color,
            flex: "0 0 auto",
          }}
        >
          {React.cloneElement(
            iconConfig.icon as React.ReactElement<{
              style?: React.CSSProperties;
            }>,
            { style: { fontSize: 22 } }
          )}
        </Avatar>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 800,
                lineHeight: 1.1,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {goal.name}
            </Typography>
            {goal.status !== "active" && (
              <Chip
                size="small"
                label={goal.status}
                color={goal.status === "completed" ? "success" : "warning"}
                sx={{ height: 20 }}
              />
            )}
          </Box>

          {goal.description && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                display: "block",
                mt: 0.25,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {goal.description}
            </Typography>
          )}

          {categories.length > 0 && (
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 0.75 }}>
              {categories.slice(0, 4).map((c) => (
                <Chip
                  key={c}
                  label={c}
                  size="small"
                  variant="outlined"
                  sx={{ fontSize: "0.7rem", height: 22 }}
                />
              ))}
              {categories.length > 4 && (
                <Chip
                  label={`+${categories.length - 4}`}
                  size="small"
                  variant="outlined"
                  sx={{ fontSize: "0.7rem", height: 22 }}
                />
              )}
            </Box>
          )}
        </Box>
      </Box>

      <Box sx={{ mt: 1.25 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "baseline",
            gap: 1,
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 900 }}>
            {formatCurrency0(goal.currentAmount)}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            of {formatCurrency0(goal.targetAmount)}
          </Typography>
        </Box>

        <Box sx={{ position: "relative", mt: 0.5 }}>
          <LinearProgress
            variant="determinate"
            value={progressPct}
            color={getProgressColor(progressPct)}
            sx={{
              height: 14,
              borderRadius: 10,
              backgroundColor: theme.palette.grey[200],
              "& .MuiLinearProgress-bar": { borderRadius: 10 },
            }}
          />
          <Typography
            variant="caption"
            sx={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontWeight: 800,
              color: progressPct > 50 ? "#fff" : theme.palette.text.primary,
              textShadow:
                progressPct > 50 ? "0 1px 2px rgba(0,0,0,0.35)" : "none",
            }}
          >
            {progressPct.toFixed(0)}%
          </Typography>
        </Box>

        <Box sx={{ display: "flex", justifyContent: "space-between", mt: 0.5 }}>
          <Typography variant="caption" color="text.secondary">
            {formatCurrency0(goal.targetAmount - goal.currentAmount)} to go
          </Typography>
          <Typography
            variant="caption"
            sx={{
              color:
                daysRemaining < 0
                  ? theme.palette.error.main
                  : daysRemaining <= 30
                    ? theme.palette.warning.main
                    : theme.palette.text.secondary,
              fontWeight: 700,
            }}
          >
            {daysRemaining < 0
              ? `${Math.abs(daysRemaining)}d overdue`
              : `${daysRemaining}d`}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default GoalGraphNode;
